import type { MockRecord } from './database.ts';
import { db } from './helpers.ts';
import { accessProfileFor } from './accessProfile.ts';
import { assignedRoleScopes, permissionScope } from './contracts/accessEvaluator.ts';
import type { AccessContext, AccessPermissionKey } from './contracts/accessModel.ts';

export const normalized = (value: unknown) => String(value ?? '').trim().toLowerCase();
const emptyId = '00000000-0000-0000-0000-000000000000';
const globalAdminNames = new Set(['administrator', 'admin', 'superadmin', 'qcdocadmin']);
const scopedAdminNames = new Set(['administrator - psl', 'psladmin', 'adminpsl', 'psladministrator', 'psl admin', 'psl administrator']);

export type AdminAssignment = { roleId: string; roleName: string; pslId: string | null };
export interface AdministrationScope { global: boolean; pslIds: Set<string> }

function assignedPslIds(profile: ReturnType<typeof accessProfileFor>): Set<string> {
  if (profile.permissionGrantVersion === 4) {
    const roleIds = new Set((profile.roleMemberships ?? []).filter((membership) => membership.userId === profile.userId).map((membership) => membership.roleId));
    return new Set((profile.rolePermissionGrants ?? []).filter((grant) => roleIds.has(grant.roleId)
      && profile.roles.some((role) => role.roleId === grant.roleId && role.isActive)).map((grant) => grant.pslId));
  }
  return new Set(assignedRoleScopes(profile).filter((assignment) => profile.roles.some((role) => role.roleId === assignment.roleId && role.isActive))
    .map((assignment) => assignment.pslId));
}

export function resolvePsl(value: unknown): MockRecord | undefined {
  const key = normalized(value);
  const pslKey = (value: unknown) => {
    const name = normalized(value).replace(/[\s-]/g, '');
    return ({ bar: 'baroid', cem: 'cementing', pe: 'productionenhancement', mc: 'multichem' } as Record<string, string>)[name] ?? name;
  };
  return key && key !== emptyId ? db().psls.find((psl) => normalized(psl.oid) === key || pslKey(psl.name) === pslKey(key)) : undefined;
}

export function rolePsl(role: Record<string, unknown>): MockRecord | undefined {
  const explicit = role.pslId ?? role.pslID ?? role.pslid ?? role.PSLID;
  if (explicit && normalized(explicit) !== emptyId) return resolvePsl(explicit);
  return resolvePsl(normalized(role.roleName).match(/^.+?\s*-\s*(.+)$/)?.[1]);
}

export function isAdministratorRole(role: Record<string, unknown>): boolean {
  const name = normalized(role.roleName);
  return globalAdminNames.has(name) || scopedAdminNames.has(name) ||
    !!(rolePsl(role) && globalAdminNames.has(name.match(/^(.+?)\s*-\s*.+$/)?.[1].trim() ?? ''));
}

export function assignments(user: MockRecord): AdminAssignment[] {
  const source = Array.isArray(user.roleAssignments) ? user.roleAssignments : Array.isArray(user.roles) ? user.roles : [];
  return source.map((entry: unknown) => {
    const raw: Record<string, unknown> = typeof entry === 'object' && entry ? entry as Record<string, unknown> : { roleId: String(entry) };
    const definition = db().roles.find((role) => normalized(role.roleId) === normalized(raw.roleId ?? raw.roleName) || normalized(role.roleName) === normalized(raw.roleName));
    const byName = definition ?? db().roles.find((role) => normalized(role.roleName) === normalized(raw.roleId));
    const merged: Record<string, unknown> = { ...byName, ...raw, roleName: raw.roleName ?? byName?.roleName ?? raw.roleId };
    const explicit = merged.pslId ?? merged.pslID ?? merged.pslid ?? merged.PSLID;
    // An unresolvable explicit scope must not collapse into a global administrator grant.
    const pslId = rolePsl(merged)?.oid ?? (explicit && normalized(explicit) !== emptyId ? String(explicit) : null);
    return { roleId: String(byName?.roleId ?? raw.roleId ?? raw.roleName), roleName: String(merged.roleName), pslId };
  });
}

export function administrationScope(user: MockRecord, permission: AccessPermissionKey = 'Administration.Users.Manage'): AdministrationScope {
  const scope: AdministrationScope = { global: false, pslIds: new Set() };
  if (user.portal !== 'QCDOC') return scope;
  if (user.accessProfile || db().accessMigrations?.length || db().accessRoles || db().userRoleAssignments) {
    return permissionScope(accessProfileFor(user), permission);
  }
  for (const role of assignments(user)) {
    if (!isAdministratorRole(role)) continue;
    if (role.pslId) {
      const psl = resolvePsl(role.pslId);
      if (psl) scope.pslIds.add(psl.oid);
    }
    else if (globalAdminNames.has(normalized(role.roleName))) scope.global = true;
  }
  return scope;
}

export function assertAdministrator(user: MockRecord, permission: AccessPermissionKey = 'Administration.Users.Manage'): AdministrationScope {
  const scope = administrationScope(user, permission);
  if (!scope.global && !scope.pslIds.size) throw new Error(`This account does not have ${permission} permission for administration${permission === 'Administration.Roles.Manage' ? '; global authority is required' : ''}.`);
  return scope;
}

export function profilePsl(record: Record<string, unknown>): MockRecord | undefined {
  return resolvePsl(record.pslId ?? record.pslID ?? record.pslid ?? record.PSLID) ?? resolvePsl(record.psl ?? record.pslName);
}

export function inAdministrationScope(scope: AdministrationScope, record: Record<string, unknown>): boolean {
  if (record.portal === 'QCDOC' && (record.roleScopeVersion === 3 || db().roleScopeMigrations?.length || db().permissionGrantMigrations?.length)) {
    if (scope.global) return true;
    const profile = accessProfileFor(record as MockRecord);
    if (!assignedPslIds(profile).size && Array.isArray(record.pendingAssignmentPslIds)) {
      return record.pendingAssignmentPslIds.some((pslId) => scope.pslIds.has(String(pslId))
        && profile.psls.some((psl) => psl.pslId === pslId && psl.isActive));
    }
    return [...assignedPslIds(profile)].some((pslId) => scope.pslIds.has(pslId)
      && profile.psls.some((psl) => psl.pslId === pslId && psl.isActive));
  }
  if (record.portal === 'QCDOC' && record.assignmentScopeVersion === 2) {
    if (scope.global) return true;
    const userId = String(record.userId ?? record.oid);
    return (db().userRoleAssignments ?? []).some((assignment) => String(assignment.userId) === userId
      && scope.pslIds.has(String(assignment.pslId))
      && db().psls.some((psl) => psl.oid === assignment.pslId && psl.enabled !== false && psl.isActive !== false)
      && db().accessRoles.some((role) => role.oid === assignment.roleId && role.enabled !== false && role.isActive !== false));
  }
  if (Array.isArray(record.pslNames)) return scope.global || record.pslNames.some((name) => scope.pslIds.has(resolvePsl(name)?.oid ?? ''));
  return scope.global || scope.pslIds.has(profilePsl(record)?.oid ?? '');
}

export function assertRecordScope(scope: AdministrationScope, record: Record<string, unknown>): void {
  if (!inAdministrationScope(scope, record)) throw new Error('This record is outside your administrator PSL scope. Cross-PSL changes are blocked by the conservative demo policy.');
}

export function scopedAccessProfile(profile: AccessContext, scope: AdministrationScope): AccessContext {
  if (scope.global) return profile;
  const psls = profile.psls.filter((psl) => psl.isActive && scope.pslIds.has(psl.pslId));
  const pslIds = new Set(psls.map((psl) => psl.pslId));
  const grants = (profile.rolePermissionGrants ?? []).filter((grant) => pslIds.has(grant.pslId)
    && profile.permissions.find((permission) => permission.permissionKey === grant.permissionId)?.scopeRule !== 'global-only');
  const visibleRoles = new Set(grants.map((grant) => grant.roleId));
  const assignments = profile.assignments.filter((assignment) => pslIds.has(assignment.pslId));
  for (const assignment of assignments) visibleRoles.add(assignment.roleId);
  if (profile.roleScopeVersion === 3) {
    for (const role of profile.roles) if (role.pslIds?.some((pslId) => pslIds.has(pslId))) visibleRoles.add(role.roleId);
  }
  return { ...profile, psls, profilePslIds: profile.profilePslIds?.filter((pslId) => pslIds.has(pslId)),
    roles: profile.roles.filter((role) => visibleRoles.has(role.roleId)).map((role) => ({ ...role,
      ...(role.pslIds ? { pslIds: role.pslIds.filter((pslId) => pslIds.has(pslId)) } : {}) })),
    assignments, roleMemberships: profile.roleMemberships?.filter((membership) => visibleRoles.has(membership.roleId)),
    rolePermissionGrants: grants,
    rolePermissions: profile.rolePermissions.filter((permission) => visibleRoles.has(permission.roleId)),
    rolePslPermissions: profile.rolePslPermissions.filter((permission) => visibleRoles.has(permission.roleId) && pslIds.has(permission.pslId)),
  };
}

export function assertUserWriteScope(scope: AdministrationScope, user: MockRecord, sharedIdentity = true): void {
  assertRecordScope(scope, user);
  if (sharedIdentity && !scope.global && user.portal === 'QCDOC' && (user.roleScopeVersion === 3 || db().roleScopeMigrations?.length || db().permissionGrantMigrations?.length)) {
    const profile = accessProfileFor(user);
    const foreign = [...assignedPslIds(profile)].some((pslId) => !scope.pslIds.has(pslId)
      && profile.psls.some((psl) => psl.pslId === pslId && psl.isActive));
    if (foreign) throw new Error('Shared user details or status require Global authority when foreign role scopes exist.');
  }
  if (sharedIdentity && !scope.global && user.portal === 'QCDOC' && user.assignmentScopeVersion === 2) {
    const userId = String(user.userId ?? user.oid);
    const foreign = (db().userRoleAssignments ?? []).some((assignment) => String(assignment.userId) === userId
      && !scope.pslIds.has(String(assignment.pslId))
      && db().psls.some((psl) => psl.oid === assignment.pslId && psl.enabled !== false && psl.isActive !== false)
      && db().accessRoles.some((role) => role.oid === assignment.roleId && role.enabled !== false && role.isActive !== false));
    if (foreign) throw new Error('Shared user details or status require Global authority when foreign assignments exist.');
  }
  const protectedPermissions = accessProfileFor(user).permissions.filter((permission) =>
    permission.permissionKey.startsWith('Administration.') || permission.scopeRule === 'global-only');
  if (!scope.global && (administrationScope(user).global
    || protectedPermissions.some((permission) => administrationScope(user, permission.permissionKey).global))) {
    throw new Error('Only a global administrator can modify a global administrator account.');
  }
}

/** Normalize profile aliases only; profile changes never create or move role assignments. */
export function profilePslPatch(input: Record<string, unknown>): Record<string, unknown> {
  if (input.pslNames !== undefined) {
    if (!Array.isArray(input.pslNames)) throw new Error('Profile PSLs must be an array.');
    if (!input.pslNames.length) throw new Error('Select at least one profile PSL.');
    const selected = input.pslNames.map((value) => {
      const psl = resolvePsl(value);
      if (!psl || psl.enabled === false || psl.isActive === false) throw new Error('Choose active profile PSLs only.');
      return psl;
    });
    const unique = [...new Map(selected.map((psl) => [psl.oid, psl])).values()];
    if (unique.some((psl) => normalized(psl.name) === 'global') && unique.length > 1) throw new Error('Global already includes all PSLs. Select Global alone.');
    return { pslNames: unique.map((psl) => String(psl.name)), profilePslIds: unique.map((psl) => psl.oid),
      psl: unique[0]?.name ?? null, pslName: unique[0]?.name ?? null, pslId: unique[0]?.oid ?? null };
  }
  const fields = Object.entries(input).filter(([key]) => ['psl', 'pslname', 'pslid'].includes(key.toLowerCase()));
  if (!fields.length) return {};
  const resolved = fields.filter(([, value]) => value !== undefined && value !== null && value !== '' && normalized(value) !== emptyId).map(([, value]) => {
    const psl = resolvePsl(value);
    if (!psl) throw new Error('Unknown profile PSL.');
    return psl;
  });
  if (new Set(resolved.map((psl) => psl.oid)).size > 1) throw new Error('Conflicting profile PSL aliases.');
  const psl = resolved[0];
  return { psl: psl?.name ?? null, pslName: psl?.name ?? null, pslId: psl?.oid ?? null };
}

export function resolveAssignment(value: unknown): AdminAssignment {
  const input = typeof value === 'object' && value ? value as Record<string, unknown> : { roleId: value };
  const key = input.roleId ?? input.roleName;
  const role = db().roles.find((row) => normalized(row.roleId) === normalized(key) || normalized(row.roleName) === normalized(key));
  if (!role) throw new Error('Unknown role assignment.');
  const pslId = rolePsl(role)?.oid ?? null;
  if (input.pslId !== undefined && (resolvePsl(input.pslId)?.oid ?? null) !== pslId) throw new Error('Role PSL does not match its definition.');
  return { roleId: String(role.roleId), roleName: String(role.roleName), pslId };
}

export function canAssignRole(scope: AdministrationScope, role: AdminAssignment, vendor = false): boolean {
  const definition = db().roles.find((entry) => entry.roleId === role.roleId);
  if ((definition?.portal === 'QCDOC-VENDOR') !== vendor) return false;
  return scope.global || (vendor ? !isAdministratorRole(role) : !!role.pslId && scope.pslIds.has(role.pslId));
}

export function replacementAssignments(scope: AdministrationScope, user: MockRecord, input: unknown): AdminAssignment[] {
  if (!Array.isArray(input)) throw new Error('Roles must be an array.');
  const existing = assignments(user);
  const vendor = user.portal === 'QCDOC-VENDOR';
  const selected = input.map((value: unknown) => {
    const key = typeof value === 'object' && value ? (value as Record<string, unknown>).roleId ?? (value as Record<string, unknown>).roleName : value;
    const previous = existing.find((role) => normalized(role.roleId) === normalized(key) || normalized(role.roleName) === normalized(key));
    // Preserve unmodeled legacy assignments, but never permit introducing an unknown one.
    if (previous && !db().roles.some((role) => role.roleId === previous.roleId)) return previous;
    return resolveAssignment(value);
  });
  for (const role of selected) {
    const alreadyAssigned = existing.some((entry) => entry.roleId === role.roleId && entry.pslId === role.pslId);
    if (!canAssignRole(scope, role, vendor) && !alreadyAssigned) throw new Error('Cannot introduce a global or out-of-scope role assignment.');
    if (!alreadyAssigned && db().roles.find((entry) => entry.roleId === role.roleId)?.enabled === false) throw new Error('Disabled roles cannot be assigned.');
  }
  // A scoped replacement replaces only the editable subset, never hidden assignments.
  const preserved = existing.filter((role) => !canAssignRole(scope, role, vendor));
  return [...new Map([...preserved, ...selected].map((role) => [`${role.roleId}:${role.pslId}`, role])).values()];
}

export function assignmentPatch(roles: AdminAssignment[]) {
  return { roles: roles.map((role) => role.roleName), roleAssignments: roles };
}